import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api';
import { Button, Label, TextInput, Textarea, Card, Alert, Spinner, Table, Select } from 'flowbite-react';
import { useParams } from 'react-router-dom';

export default function Broadcasts() {
  const { appUuid: routeAppUuid } = useParams();
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ application_id: '', message: '', segment: 'all' });
  const [recipients, setRecipients] = useState(null);
  const [appUuid, setAppUuid] = useState(routeAppUuid || '');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = appUuid ? `?app_uuid=${encodeURIComponent(appUuid)}` : '';
      const data = await api.get(`/broadcasts${qs}`);
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [appUuid]);

  const loadRecipients = useCallback(async () => {
    try {
      const application_id = routeAppUuid || form.application_id;
      if (!application_id) {
        setRecipients(null);
        return;
      }
      const seg = form.segment && form.segment !== 'all' ? `&segment=${encodeURIComponent(form.segment)}` : '';
      const data = await api.get(`/broadcasts/recipients?application_id=${encodeURIComponent(application_id)}${seg}`);
      setRecipients(data.count || 0);
    } catch (e) {
      setRecipients(null);
    }
  }, [routeAppUuid, form.application_id, form.segment]);

  useEffect(() => { setAppUuid(routeAppUuid || ''); }, [routeAppUuid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRecipients(); }, [loadRecipients]);

  const submit = async (e) => {
    e.preventDefault();
    if (!(routeAppUuid || form.application_id) || !form.message) return;
    setError('');
    try {
      const payload = { application_id: routeAppUuid || form.application_id, message: form.message };
      if (form.segment && form.segment !== 'all') payload.segment = form.segment;
      await api.post('/broadcasts', payload);
      setForm({ application_id: '', message: '', segment: 'all' });
      setRecipients(null);
      await load();
      await loadRecipients();
    } catch (e) {
      setError(e.message);
    }
  };

  const newOffers = async () => {
    if (!(routeAppUuid || form.application_id)) {
      setError('Укажите application_id');
      return;
    }
    try {
      if (routeAppUuid) {
        await api.post(`/broadcasts/new-offers?application_id=${encodeURIComponent(routeAppUuid)}`, {});
      } else {
        await api.post(`/broadcasts/new-offers?application_id=${encodeURIComponent(form.application_id)}`, {});
      }
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const applyFilter = async (e) => {
    e.preventDefault();
    await load();
  };

  return (
    <div className="grid gap-4">
      <Card>
        <h2 className="text-lg font-semibold">Рассылки</h2>
        {error && <Alert color="failure" className="mt-2">{error}</Alert>}
        {!routeAppUuid && (
          <form onSubmit={applyFilter} className="flex items-end gap-2 mb-3 mt-2">
            <div className="flex-1">
              <Label htmlFor="app_uuid" value="app_uuid" />
              <TextInput id="app_uuid" placeholder="app_uuid" value={appUuid} onChange={e => setAppUuid(e.target.value)} />
            </div>
            <Button type="submit">Фильтровать</Button>
          </form>
        )}
        {loading ? (
          <div className="py-4"><Spinner /></div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="grid gap-3 sm:hidden">
              {items.length === 0 ? (
                <div className="text-sm text-gray-500">История рассылок пуста</div>
              ) : (
                items.map(b => (
                  <Card key={b.id}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold">#{b.id}</div>
                        <div className="text-xs text-gray-500 mt-1">{new Date(b.created_at).toLocaleString()}</div>
                      </div>
                      <div className="text-right text-sm">
                        <div className="text-green-700">Успех: {b.success_count}</div>
                        <div className="text-red-700">Ошибка: {b.fail_count}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-sm break-words">{b.message}</div>
                  </Card>
                ))
              )}
            </div>
            {/* Desktop table */}
            <div className="overflow-x-auto hidden sm:block">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>ID</Table.HeadCell>
                  <Table.HeadCell className="hidden sm:table-cell">Создано</Table.HeadCell>
                  <Table.HeadCell className="hidden md:table-cell">Успех</Table.HeadCell>
                  <Table.HeadCell className="hidden md:table-cell">Ошибка</Table.HeadCell>
                  <Table.HeadCell>Сообщение</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {items.map(b => (
                    <Table.Row key={b.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                      <Table.Cell>#{b.id}</Table.Cell>
                      <Table.Cell className="hidden sm:table-cell whitespace-nowrap">{new Date(b.created_at).toLocaleString()}</Table.Cell>
                      <Table.Cell className="hidden md:table-cell text-green-700">{b.success_count}</Table.Cell>
                      <Table.Cell className="hidden md:table-cell text-red-700">{b.fail_count}</Table.Cell>
                      <Table.Cell className="max-w-[240px] sm:max-w-none break-words">{b.message}</Table.Cell>
                    </Table.Row>
                  ))}
                  {items.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={5} className="text-center text-sm text-gray-500">История рассылок пуста</Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
          </>
        )}
      </Card>
      <Card>
        <h3 className="text-base font-semibold">Отправить рассылку</h3>
        <form onSubmit={submit} className="grid gap-3">
          {!routeAppUuid && (
            <div>
              <Label value="application_id" />
              <TextInput value={form.application_id} onChange={e => setForm({ ...form, application_id: e.target.value })} placeholder="application_id" />
            </div>
          )}
          <div>
            <Label value="Сегмент" />
            <Select value={form.segment} onChange={e => setForm({ ...form, segment: e.target.value })}>
              <option value="all">Все пользователи</option>
              <option value="active_30d">Активные за 30 дней</option>
            </Select>
            {recipients !== null && (
              <div className="text-xs text-gray-500 mt-1">Получателей: {recipients}</div>
            )}
          </div>
          <div>
            <Label value="Сообщение" />
            <Textarea rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Текст сообщения" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit">Отправить</Button>
            <Button color="light" type="button" onClick={newOffers}>Новые предложения</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}